import { useEffect, useState } from "react";
import { sgo } from "@/lib/api";
import { SgoShell } from "@/components/SgoShell";
import { Search, Plus, Edit3, X, Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

export default function SgoClientes() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => sgo.clientes(q).then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const buscar = () => load();
  const emptyForm = { razao_social: "", nome_fantasia: "", cnpj: "", municipio: "", uf: "SP", lider: "", lider_contato: "", telefone: "", email: "", endereco: "", status: "ativo" };

  const salvar = async (form) => {
    setLoading(true);
    try {
      if (editing?.id) {
        await sgo.editarCliente(editing.id, form);
        toast.success("Cliente atualizado");
      } else {
        await sgo.criarCliente(form);
        toast.success("Cliente cadastrado");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setLoading(false); }
  };

  return (
    <SgoShell activeModule="clientes">
      <div className="p-5 md:p-8 fade-up">
        {/* Barra de busca + Novo Cliente */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex-1 bg-white rounded-xl border border-[#B28F4B]/15 shadow-sm px-4 py-3 flex items-center gap-3">
            <Search size={18} className="text-[#B28F4B]"/>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === "Enter" && buscar()}
              placeholder="Buscar por razão social, CNPJ ou município..."
              className="flex-1 bg-transparent outline-none text-[#1A1D42] placeholder:text-[#6B6E8F] text-sm"
              data-testid="input-buscar-cliente"
            />
            {q && <button onClick={() => { setQ(""); setTimeout(load, 0); }} className="text-[#6B6E8F] hover:text-[#1A1D42] text-xs font-bold">Limpar</button>}
          </div>
          <button
            onClick={() => setEditing({ ...emptyForm })}
            data-testid="btn-novo-cliente"
            className="bg-[#1A1D42] hover:bg-[#252952] text-[#B28F4B] px-6 py-3 rounded-xl font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg border border-[#B28F4B]/40"
          >
            <Plus size={16}/> Novo Cliente
          </button>
        </div>

        {/* Tabela de clientes (fundo claro estilo original) */}
        <div className="bg-white rounded-2xl shadow-lg border border-[#B28F4B]/10 overflow-hidden">
          {/* Header da tabela */}
          <div className="grid grid-cols-12 gap-3 px-6 py-4 border-b-2 border-[#1A1D42]/10 text-[10px] font-bold uppercase tracking-widest text-[#6B6E8F]">
            <div className="col-span-2">Cód.</div>
            <div className="col-span-3">Razão Social / Fantasia</div>
            <div className="col-span-2">CNPJ</div>
            <div className="col-span-2">Município</div>
            <div className="col-span-1">Líder</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-right">Ações</div>
          </div>

          {items.length === 0 && (
            <div className="p-12 text-center">
              <Building2 size={44} className="mx-auto text-[#B28F4B]/40 mb-3"/>
              <div className="text-[#1A1D42] font-heading text-lg mb-1">Nenhum cliente cadastrado</div>
              <div className="text-[#6B6E8F] text-sm">Clique em "Novo Cliente" para começar</div>
            </div>
          )}

          {items.map((c, i) => (
            <div
              key={c.id}
              data-testid={`cliente-row-${c.id}`}
              className={`grid grid-cols-12 gap-3 px-6 py-5 items-center border-b border-[#1A1D42]/5 hover:bg-[#F8FAFC] transition-colors ${i === items.length - 1 ? "border-b-0" : ""}`}
            >
              <div className="col-span-2">
                <div className="font-mono text-[#1A1D42] text-sm font-bold tracking-wider">{c.codigo}</div>
              </div>
              <div className="col-span-3">
                <div className="text-[#1A1D42] font-bold text-sm leading-tight">{c.razao_social}</div>
                {c.nome_fantasia && <div className="text-[#6B6E8F] text-xs mt-0.5">{c.nome_fantasia}</div>}
              </div>
              <div className="col-span-2">
                <div className="text-[#475569] text-sm font-mono">{c.cnpj}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[#475569] text-sm">{c.municipio} {c.uf && `/ ${c.uf}`}</div>
              </div>
              <div className="col-span-1">
                <div className="text-[#475569] text-xs">{c.lider || "—"}</div>
              </div>
              <div className="col-span-1">
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                  c.status === "ativo" ? "bg-emerald-100 text-emerald-700 border border-emerald-300" : "bg-red-100 text-red-700 border border-red-300"
                }`}>
                  {c.status === "ativo" ? "Ativo" : "Inativo"}
                </span>
              </div>
              <div className="col-span-1 flex items-center justify-end gap-1.5">
                <button
                  onClick={() => setEditing(c)}
                  data-testid={`btn-editar-cliente-${c.id}`}
                  className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 transition"
                  title="Editar"
                >
                  <Edit3 size={13}/>
                </button>
                <a
                  href="/orcamentos"
                  data-testid={`btn-usar-cliente-${c.id}`}
                  className="px-2.5 py-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-xs font-bold uppercase tracking-wider transition inline-flex items-center gap-1"
                  title="Usar em orçamento"
                >
                  📊 Usar
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <ClienteModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={salvar}
          loading={loading}
        />
      )}
    </SgoShell>
  );
}

function ClienteModal({ initial, onClose, onSave, loading }) {
  const [form, setForm] = useState(initial);
  const handle = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 fade-up" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header modal */}
        <div className="bg-gradient-to-r from-[#1A1D42] to-[#252952] px-6 py-5 flex items-center justify-between rounded-t-2xl">
          <div>
            <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.3em] uppercase mb-1">Cadastro de Cliente</div>
            <h2 className="font-heading text-xl text-white">{initial?.id ? "Editar" : "Novo cliente"}</h2>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1"><X size={20}/></button>
        </div>

        <div className="p-6 space-y-4">
          <F label="Razão Social" required>
            <input value={form.razao_social} onChange={e => handle("razao_social", e.target.value)} className="inp" data-testid="input-razao-social"/>
          </F>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Nome Fantasia">
              <input value={form.nome_fantasia || ""} onChange={e => handle("nome_fantasia", e.target.value)} className="inp"/>
            </F>
            <F label="CNPJ" required>
              <input value={form.cnpj} onChange={e => handle("cnpj", e.target.value)} className="inp" placeholder="00.000.000/0000-00" data-testid="input-cnpj"/>
            </F>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <F label="Município">
              <input value={form.municipio || ""} onChange={e => handle("municipio", e.target.value)} className="inp"/>
            </F>
            <F label="UF">
              <select value={form.uf || "SP"} onChange={e => handle("uf", e.target.value)} className="inp">
                {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
              </select>
            </F>
            <F label="Status">
              <select value={form.status} onChange={e => handle("status", e.target.value)} className="inp">
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </F>
          </div>
          <F label="Endereço"><input value={form.endereco || ""} onChange={e => handle("endereco", e.target.value)} className="inp"/></F>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Líder / Contato principal">
              <input value={form.lider || ""} onChange={e => handle("lider", e.target.value)} className="inp"/>
            </F>
            <F label="Telefone do Líder">
              <input value={form.lider_contato || ""} onChange={e => handle("lider_contato", e.target.value)} className="inp"/>
            </F>
          </div>
          <F label="E-mail"><input value={form.email || ""} onChange={e => handle("email", e.target.value)} className="inp"/></F>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button onClick={onClose} className="px-5 py-2.5 rounded-lg text-[#1A1D42] hover:bg-slate-100 font-bold text-sm">Cancelar</button>
            <button
              onClick={() => onSave(form)}
              disabled={loading || !form.razao_social || !form.cnpj}
              data-testid="btn-salvar-cliente"
              className="bg-[#1A1D42] hover:bg-[#252952] text-[#B28F4B] px-6 py-2.5 rounded-lg font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50 border border-[#B28F4B]/40"
            >
              {loading ? <Loader2 size={14} className="animate-spin"/> : null}
              {initial?.id ? "Atualizar" : "Cadastrar"}
            </button>
          </div>
        </div>
      </div>
      <style>{`.inp { width: 100%; background: #F8FAFC; border: 1.5px solid #E2E8F0; color: #1A1D42; padding: 10px 14px; border-radius: 8px; outline: none; font-size: 14px; transition: border-color 0.15s; } .inp:focus { border-color: #B28F4B; box-shadow: 0 0 0 3px rgba(178,143,75,0.15); }`}</style>
    </div>
  );
}

function F({ label, children, required }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-widest text-[#6B6E8F] block mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}
